import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";

interface PasswordConfirmContextType {
  isPasswordConfirmed: boolean;
  confirmPassword: () => void;
  resetConfirmation: () => void;
}

const PasswordConfirmContext = createContext<PasswordConfirmContextType | undefined>(undefined);

const CONFIRMATION_EXPIRY_MS = 30 * 60 * 1000; // 30 min
const STORAGE_KEY = "password_confirmation_timestamp";

export const PasswordConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [confirmationTimestamp, setConfirmationTimestamp] = useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const timestamp = parseInt(stored, 10);
      const now = Date.now();
      if (now - timestamp < CONFIRMATION_EXPIRY_MS) {
        return timestamp;
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    return null;
  });

  const isPasswordConfirmed = useCallback(() => {
    if (!confirmationTimestamp) return false;
    const now = Date.now();
    const isValid = now - confirmationTimestamp < CONFIRMATION_EXPIRY_MS;

    if (!isValid) {
      localStorage.removeItem(STORAGE_KEY);
      setConfirmationTimestamp(null);
    }

    return isValid;
  }, [confirmationTimestamp])();

  const confirmPassword = () => {
    const timestamp = Date.now();
    setConfirmationTimestamp(timestamp);
    localStorage.setItem(STORAGE_KEY, timestamp.toString());
  };

  const resetConfirmation = () => {
    setConfirmationTimestamp(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  useEffect(() => {
    if (!confirmationTimestamp) return;

    const timeLeft = CONFIRMATION_EXPIRY_MS - (Date.now() - confirmationTimestamp);

    if (timeLeft <= 0) {
      resetConfirmation();
      return;
    }

    const timer = setTimeout(() => {
      resetConfirmation();
    }, timeLeft);

    return () => clearTimeout(timer);
  }, [confirmationTimestamp]);

  return (
    <PasswordConfirmContext.Provider
      value={{
        isPasswordConfirmed,
        confirmPassword,
        resetConfirmation,
      }}
    >
      {children}
    </PasswordConfirmContext.Provider>
  );
};

export const usePasswordConfirm = () => {
  const context = useContext(PasswordConfirmContext);
  if (!context) {
    throw new Error("usePasswordConfirm must be used within PasswordConfirmProvider");
  }
  return context;
};
